/**
 * Sibling-file detection for multi-file opens.
 *
 * A text `.gltf` references external `.bin` buffers and textures; a scene
 * document (glTF 2.1, or a legacy `.glxf`) references external GLB/glTF files
 * through its manifest. When the user picks or drops several
 * files together, the companions must not be imported as standalone objects —
 * they are registered as sibling assets so the scene file's external
 * references resolve to their blobs (see ResourceManager.registerSiblingAssets
 * in trace-viewer).
 *
 * Detection is reference-precise: each selected `.gltf`/`.glxf` is parsed and
 * only files actually referenced by one of them are treated as siblings.
 * Anything else (e.g. an unrelated .png dropped in the same gesture) still
 * imports as its own object.
 */

/** External-reference-carrying formats we parse for sibling detection. */
const REF_HOST_EXTENSIONS = /\.(gltf|glxf)$/i;

export interface SiblingPartition {
    /** Files to load as scenes/objects, in selection order. */
    loadables: File[];
    /** Referenced companion files, keyed by file name (registration key). */
    siblings: Map<string, Blob>;
}

/**
 * Relative external URIs referenced by a parsed scene document:
 * `buffers[].uri` + `images[].uri` (plain glTF), `files[].uri` (glTF 2.1 —
 * the whole dependency manifest, CONTENT_MODEL_SPEC §2.1), and `assets[].uri`
 * (legacy glXF, still read).
 * `data:` URIs and absolute URLs are not local siblings.
 *
 * KEEP IN SYNC with the copy in trace-viewer/vscode/src/extension.ts —
 * the extension builds standalone (CommonJS, no bundler) and cannot import
 * this module, so the extraction rule is intentionally duplicated there.
 */
export function extractExternalUris(json: unknown): string[] {
    const doc = json as {
        buffers?: Array<{ uri?: string }>;
        images?: Array<{ uri?: string }>;
        files?: Array<{ uri?: string }>;
        assets?: Array<{ uri?: string }>;
    };
    const refs = [
        ...(doc.buffers ?? []),
        ...(doc.images ?? []),
        ...(doc.files ?? []),
        ...(doc.assets ?? []),
    ];
    return refs
        .map((r) => r.uri)
        .filter((uri): uri is string =>
            !!uri
            && !uri.startsWith('data:')
            && !/^[a-z][a-z0-9+.-]*:/i.test(uri) // any absolute scheme (http:, blob:, …)
        );
}

/**
 * Partition a multi-file selection into loadable files and sibling companions.
 *
 * Multi-select flattens directories, so matching is by file name: a reference
 * `textures/wood.png` matches a selected `wood.png`. The registration key is
 * the file name — the viewer's resolvers fall back to basename lookup.
 */
export async function partitionSiblingFiles(files: File[]): Promise<SiblingPartition> {
    const refHosts = files.filter((f) => REF_HOST_EXTENSIONS.test(f.name));
    if (refHosts.length === 0) {
        return { loadables: files, siblings: new Map() };
    }

    const referencedNames = new Set<string>();
    for (const host of refHosts) {
        let json: unknown;
        try {
            json = JSON.parse(await host.text());
        } catch {
            continue; // unparseable — nothing to match against
        }
        for (const uri of extractExternalUris(json)) {
            let decoded = uri;
            try {
                decoded = decodeURIComponent(uri); // glTF uris are percent-encoded
            } catch { /* malformed escape — use raw */ }
            // Normalize backslashes (spec-illegal but produced by some
            // Windows exporters) before taking the basename.
            const normalized = decoded.replace(/\\/g, '/');
            referencedNames.add(normalized.split('/').pop() ?? normalized);
        }
    }

    const loadables: File[] = [];
    const siblings = new Map<string, Blob>();
    for (const file of files) {
        if (!refHosts.includes(file) && referencedNames.has(file.name)) {
            siblings.set(file.name, file);
        } else {
            loadables.push(file);
        }
    }
    return { loadables, siblings };
}
